/**
 * @file 贡献指南
 * @author WanZhongYun(wanzhongyun@baidu.com)
 */
import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { Menu } from 'antd';

import Header from '../../components/header/HeaderWithStore';
import Footer from '../../components/footer/footer';
import hljs from '../../utils/highlight';
import copy from 'copy-text-to-clipboard';
import ReactStickyPolyfill from '../../components/react-sticky-polyfill/ReactStickyPolyfill';
import classNames from 'classnames';
import { message } from 'antd';
import mathJax from '../../utils/mathjax/mathjax';
import DocRightMenu from './components/doc-right-menu/DocRightMenu';
import useDocumentStore from './ContributionHook';
import { Link } from 'react-router-dom';

const ContributionGuide: React.FC = () => {
    const { menuHook, documentArticle, scrollToAnchor  } = useDocumentStore();
    const {
        anchorMenuList,
        anchorSelectedKeys,
        anchorOpenKeys,
        leftMenuShow,

        switchLeftMenuShow
    } = menuHook;


    const leftMenuIem = [
        {
            label: '社区贡献',
            value: 'community_contribution_cn'
        },
        {
            label: '代码贡献',
            value: 'local_dev_guide_cn'
        },
        {
            label: '文档贡献',
            value: 'docs_contribution'
        },
        {
            label: 'Hackathon 活动说明',
            value: 'hackathon_cn'
        }
    ];
    const markdownBodyRef = useRef<HTMLDivElement>(null);
    const leftMenu = useRef<HTMLDivElement>(null);
    const pageMainRef = useRef<HTMLDivElement>(null);
    const menuSwitch = useRef<HTMLDivElement>(null);

    // 滚动事件
    useEffect(() => {
        (async () => {
            const theMathJax = await mathJax;
            await theMathJax.Hub.Queue(['Typeset', theMathJax.Hub]);
            scrollToAnchor();
        })();
    }, [documentArticle, scrollToAnchor]);



    // 这个下面是main content
    useEffect(() => {
        if (documentArticle && markdownBodyRef.current) {
            markdownBodyRef.current.querySelectorAll<HTMLElement>('.highlight').forEach(block => {
                hljs.highlightBlock(block);
            });
        }
    }, [documentArticle]);

    useLayoutEffect(() => {
        if (markdownBodyRef.current) {
            // bca-disable-line
            markdownBodyRef.current.innerHTML = '';
            if (documentArticle) {
                markdownBodyRef.current.appendChild(documentArticle);
            }
        }
    }, [documentArticle]);

    const [themeDark, setThemeDark] = useState<boolean>(() => {
        if (window.localStorage) {
            const storage = window.localStorage;
            return !!storage.getItem('document-theme-dark');
        }
        return false;
    });

    const onThemeDarkChange = useCallback(() => {
        setThemeDark((old) => {
            if (window.localStorage) {
                const storage = window.localStorage;
                storage.setItem('document-theme-dark', !old ? '1' : '');
            }
            return !old;
        });
    }, [setThemeDark]);

    const copyBtnClick = useCallback((event: MouseEvent) => {
        const codeWrap = event.target as HTMLDivElement;
        const codeDom = codeWrap.parentNode?.parentNode?.querySelector('pre');
        const text = codeDom?.innerText || '';
        const text2 = text.length > 20 ? `${text.slice(0, 20)}...` : text;
        message.info(`${text2} 已复制到剪贴板`);
        copy(text);
    }, []);


    // 复制按钮、主题按钮
    useEffect(() => {
        const dom = markdownBodyRef.current;

        if (dom) {
            const btnDomList: HTMLElement[] = [];
            const allCodeList = Array.from(dom.querySelectorAll<HTMLDivElement>('.highlight'));
            allCodeList.forEach((codeDiv) => {
                const btnWrap = document.createElement('div');
                btnWrap.className = 'btn-wrap';

                (() => {
                    const newBtn = document.createElement('div');
                    btnDomList.push(newBtn);
                    newBtn.className = 'code-copy-btn';
                    newBtn.title = '复制到剪贴板';
                    btnWrap.appendChild(newBtn);
                    codeDiv.appendChild(btnWrap);
                    newBtn.addEventListener('click', copyBtnClick);
                })();

                (() => {
                    const newBtn = document.createElement('div');
                    btnDomList.push(newBtn);
                    newBtn.className = 'code-theme-btn';
                    btnWrap.appendChild(newBtn);
                    codeDiv.appendChild(btnWrap);
                    newBtn.addEventListener('click', onThemeDarkChange);
                })();
            });
            const inputCodeList = Array.from(dom.querySelectorAll<HTMLDivElement>('.highlight-text .highlight'));
            inputCodeList.forEach((codeDiv) => {
                const btnWrap = document.createElement('div');
                btnWrap.className = 'btn-wrap';

                const newBtn = document.createElement('div');
                btnDomList.push(newBtn);
                newBtn.className = 'code-theme-btn';
                btnWrap.appendChild(newBtn);
                codeDiv.appendChild(btnWrap);
                newBtn.addEventListener('click', onThemeDarkChange);
            });

            return () => {
                btnDomList.forEach((btnDom) => {
                    btnDom.parentNode?.removeChild(btnDom);
                });
            };
        }
    }, [onThemeDarkChange, copyBtnClick, documentArticle]);
    // 这个上面是main content


    useEffect(() => {
        if (markdownBodyRef.current && documentArticle) {
            markdownBodyRef.current.appendChild(documentArticle);
        }
        if (leftMenu.current && menuSwitch.current && documentArticle) {
            leftMenu.current.style.display = 'block';
            menuSwitch.current.style.display = 'block';
        }
    }, [documentArticle]);


    return (
        <div>
            <Header />
            <div className="paddle-contribution-doc-page-container">
                <div
                    className={classNames(
                        'paddle-doc-page-main',
                        {
                            'paddle-doc-page-left-menu-show': leftMenuShow
                        }
                    )}
                    ref={pageMainRef}
                >
                    <div className="paddle-doc-page-left" ref={leftMenu}>
                        <Menu className="doc-page-left-nav" defaultSelectedKeys={window.location.search ? [window.location.search.split('=')[1]] : ['community_contribution_cn']}>
                            {
                                leftMenuIem.map(item => <Menu.Item key={item.value}><Link to={`/contributionguide?docPath=${item.value}`}>{item.label}</Link></Menu.Item>)
                            }
                        </Menu>
                    </div>
                    <div className="paddle-doc-page-right">
                        <ReactStickyPolyfill>
                            <div className="paddle-doc-page-right-menu-wrap">
                                <div className="paddle-doc-page-right-menu-index">
                                    目录
                                </div>
                                <DocRightMenu
                                    anchorOpenKeys={anchorOpenKeys}
                                    anchorSelectedKeys={anchorSelectedKeys}
                                    anchorMenuList={anchorMenuList}
                                />
                            </div>
                        </ReactStickyPolyfill>
                    </div>
                    <div className="paddle-doc-page-center">
                        <ReactStickyPolyfill>
                            <div className="paddle-doc-page-left-menu-switch-wrap" ref={menuSwitch}>
                                <div className="paddle-doc-page-left-menu-switch" onClick={switchLeftMenuShow} />
                            </div>
                        </ReactStickyPolyfill>
                        <div
                            ref={markdownBodyRef}
                            className={classNames(
                                'paddle-doc-page-content',
                                'markdown-body',
                                themeDark && 'theme-code-dark'
                            )}
                        >
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ContributionGuide;